// Stage 46: 4-stop linear-RGB palettes for the LargeDotWidget
// fragment-shader wallpaper. Keys mirror the 12 ZeroPersona
// mood labels (lowercase). Stop order: 0 = darkest ink, 3 =
// brightest highlight. Designed to read against the chat panel's
// --bg-soft. The dominant mid-tone (stop 2) tracks the existing
// MOOD_PALETTE hex anchors so the small header DOT and the
// large wallpaper feel keyed to the same colour decisions.

export type RGB = readonly [number, number, number];
export type WallpaperPalette = readonly [RGB, RGB, RGB, RGB];

export const MOOD_WALLPAPER_PALETTES: Record<string, WallpaperPalette> = {
  calm: [
    [0.02, 0.04, 0.05],
    [0.06, 0.18, 0.22],
    [0.3, 0.62, 0.7],
    [0.78, 0.92, 0.94],
  ],
  curious: [
    [0.04, 0.02, 0.08],
    [0.2, 0.08, 0.32],
    [0.56, 0.34, 0.82],
    [0.88, 0.78, 0.98],
  ],
  focused: [
    [0.02, 0.04, 0.08],
    [0.08, 0.16, 0.3],
    [0.36, 0.5, 0.85],
    [0.82, 0.88, 0.98],
  ],
  playful: [
    [0.05, 0.03, 0.02],
    [0.3, 0.18, 0.08],
    [0.95, 0.62, 0.3],
    [1.0, 0.92, 0.65],
  ],
  thoughtful: [
    [0.02, 0.03, 0.05],
    [0.1, 0.12, 0.18],
    [0.4, 0.46, 0.62],
    [0.8, 0.82, 0.9],
  ],
  wry: [
    [0.03, 0.025, 0.02],
    [0.15, 0.12, 0.08],
    [0.62, 0.5, 0.3],
    [0.9, 0.84, 0.7],
  ],
  earnest: [
    [0.02, 0.04, 0.03],
    [0.08, 0.2, 0.12],
    [0.32, 0.74, 0.5],
    [0.84, 0.96, 0.88],
  ],
  warm: [
    [0.05, 0.02, 0.02],
    [0.28, 0.1, 0.06],
    [0.95, 0.48, 0.3],
    [1.0, 0.9, 0.72],
  ],
  restless: [
    [0.05, 0.02, 0.03],
    [0.3, 0.06, 0.16],
    [0.95, 0.32, 0.5],
    [1.0, 0.86, 0.8],
  ],
  sardonic: [
    [0.03, 0.03, 0.06],
    [0.14, 0.1, 0.28],
    [0.5, 0.42, 0.85],
    [0.9, 0.86, 0.98],
  ],
  resolute: [
    [0.02, 0.04, 0.06],
    [0.04, 0.16, 0.3],
    [0.2, 0.62, 0.95],
    [0.78, 0.9, 1.0],
  ],
  contemplative: [
    [0.02, 0.03, 0.04],
    [0.06, 0.1, 0.16],
    [0.26, 0.4, 0.56],
    [0.74, 0.84, 0.94],
  ],
};

/**
 * Default palette used when the mood label is unknown / null AND
 * the user has mood-bg enabled. Keyed to the Stage 45 fallback
 * hex (#7dd3fc) but still 4-stop and animated.
 */
export const WALLPAPER_PALETTE_FALLBACK: WallpaperPalette = [
  [0.02, 0.04, 0.06],
  [0.06, 0.2, 0.3],
  [0.3, 0.66, 0.84],
  [0.82, 0.94, 0.99],
];

/**
 * Build a monochrome 4-stop palette from a single hex color, used
 * when moodBackgroundEnabled === false. Stops are linear scales of
 * the input colour at {0.05, 0.20, 0.55, 0.90} so the field still
 * reads as a wallpaper rather than collapsing to flat black.
 */
export function buildMonoPalette(hex: string): WallpaperPalette {
  const [r, g, b] = hexToLinearRgb(hex);
  const scale = (k: number): RGB =>
    [
      Math.min(1, r * k + 0.02),
      Math.min(1, g * k + 0.02),
      Math.min(1, b * k + 0.02),
    ] as const;
  return [scale(0.05), scale(0.2), scale(0.55), scale(0.9)] as const;
}

function hexToLinearRgb(hex: string): RGB {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return [0.5, 0.83, 0.99];
  const n = parseInt(m[1], 16);
  return [
    ((n >> 16) & 0xff) / 255,
    ((n >> 8) & 0xff) / 255,
    (n & 0xff) / 255,
  ];
}

export function paletteForMood(
  label: string | null | undefined,
): WallpaperPalette {
  if (!label) return WALLPAPER_PALETTE_FALLBACK;
  const k = label.toLowerCase();
  return MOOD_WALLPAPER_PALETTES[k] ?? WALLPAPER_PALETTE_FALLBACK;
}
